import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  VStack,
  HStack,
  Heading,
  Box,
  Icon,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Text,
  Button,
  Image,
  SimpleGrid,
  Badge,
  useToast,
  Center,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Divider
} from '@chakra-ui/react';
import { FaGamepad, FaCoins, FaChess, FaTrophy, FaGift, FaCalendarAlt, FaTicketAlt, FaArrowLeft, FaPlus, FaPlay } from 'react-icons/fa';
import { GiCardRandom } from 'react-icons/gi';
import { GiPokerHand } from 'react-icons/gi';
import { GiCardAceSpades, GiTreasureMap, GiCardPlay, GiChest } from 'react-icons/gi';
import FortuneWheel from '../game/components/GameUI/FortuneWheel';
import GiftBoxGame from '../game/components/GameUI/GiftBoxGame';
import CardGrid from '../game/components/GameUI/CardGrid';
import GamePanel from '../game/components/GameUI/GamePanel';
import ChestBonus from '../game/components/GameUI/ChestBonus';
import DailyBonus from '../game/components/GameUI/DailyBonus';
import DailyPrizeCard from '../game/components/GameUI/DailyPrizeCard';
// Updated imports for scratch card components
import BasicScratchCard from '../game/components/ScratchCards/ScratchCardGame';
import ScratchCardDisplay from '../game/components/ScratchCards/ScratchCardDisplay';
import QuickCashGame from '../game/components/ScratchCards/Games/QuickCash/QuickCashGame';
import BountyPool from '../game/components/GameUI/BountyPool';
import SlotMachine from '../game/components/GameUI/SlotMachine';
import PalmHeistProgressMap from '../game/components/GameUI/PalmHeistProgressMap';
import FullCardGame from '../game/components/GameUI/FullCardGame';
import MainCardGame from './MainCardGame';

// Import game creation components
import GameCreationWizard from '../game/components/ScratchCards/GameCreation/GameCreationWizard';
import QuickCashCreator from '../game/components/ScratchCards/GameCreation/Templates/QuickCashCreator';

// Import the new centralized card configurations
import { 
  QUICK_CASH_CONFIG, 
  CASH_KING_CONFIG, 
  JACKPOT_MILLIONS_CONFIG, 
  MEGA_MILLIONS_CONFIG,
  ALL_CARD_CONFIGS
} from '../game/components/ScratchCards/Games/ScratchCardConfigs';

// Island Map Game Component
const IslandMapGame = () => {
  const [userStats, setUserStats] = useState({
    totalWagered: 0,
    gamesPlayed: 0,
    winCount: 0,
    lossCount: 0,
    consecutiveDays: 1
  });
  const [balance, setBalance] = useState(1000); // Mock balance in SOL
  const toast = useToast();

  // Simulate player progress
  useEffect(() => {
    // This would normally come from your game state or blockchain
    const savedStats = localStorage.getItem('palmHeistPlayerStats');
    if (savedStats) {
      setUserStats(JSON.parse(savedStats));
    }
  }, []);

  // Handle claiming rewards
  const handleRewardClaim = (amount) => {
    setBalance(prev => prev + amount);
    toast({
      title: "Reward Claimed!",
      description: `${amount} SOL has been added to your balance!`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  // Handle playing a game
  const handlePlayGame = () => {
    // Simulate playing a game with current RTP
    const currentZone = Math.floor(userStats.totalWagered / 100) + 1;
    const zoneRtp = currentZone <= 3 ? 55 : (currentZone >= 10 ? 120 : 75 + (currentZone - 1) * 5); // RTP calculation based on zone
    const betAmount = 10; // Fixed bet amount for this example
    
    // Update stats
    const newStats = {
      ...userStats,
      totalWagered: userStats.totalWagered + betAmount,
      gamesPlayed: userStats.gamesPlayed + 1
    };
    
    // Determine win/loss based on RTP
    const isWin = Math.random() * 100 < zoneRtp;
    if (isWin) {
      const winAmount = betAmount * (zoneRtp / 100); // Win calculation based on RTP
      setBalance(prev => prev + winAmount);
      newStats.winCount = userStats.winCount + 1;
      
      toast({
        title: "You Won!",
        description: `Congratulations! You won ${winAmount.toFixed(2)} SOL with ${zoneRtp}% RTP!`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } else {
      setBalance(prev => prev - betAmount);
      newStats.lossCount = userStats.lossCount + 1;
      
      toast({
        title: "You Lost",
        description: `Better luck next time. You lost ${betAmount} SOL.`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
    
    // Save updated stats
    setUserStats(newStats);
    localStorage.setItem('palmHeistPlayerStats', JSON.stringify(newStats));
  };

  return (
    <Box>
      <PalmHeistProgressMap 
        userStats={userStats}
        onPlayGame={handlePlayGame}
        balance={balance}
      />
    </Box>
  );
};

const ScratchCardGame = () => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [creatorMode, setCreatorMode] = useState(false);
  const [templateId, setTemplateId] = useState(null);
  const [userCreatedGames, setUserCreatedGames] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    // First check for path parameters in the URL
    const pathParts = location.pathname.split('/');
    if (pathParts.length >= 5 && pathParts[3] === 'scratch-cards') {
      const cardType = pathParts[4];
      if (['quick_cash', 'cash_king', 'jackpot_millions', 'mega_millions'].includes(cardType)) {
        console.log('Found card type in URL path:', cardType);
        setSelectedCard(cardType);
        setCreatorMode(false);
        return;
      }
      
      // Check for creator mode in the path
      if (cardType === 'create') {
        setCreatorMode(true);
        return;
      }
    }
    
    // Fallback to query parameters for backward compatibility
    const params = new URLSearchParams(location.search);
    const cardTypeParam = params.get('cardType');
    const mode = params.get('mode');
    const template = params.get('template');
    
    // Check if we're in creator mode
    if (mode === 'creator') {
      setCreatorMode(true);
      setTemplateId(template);
    }
    // If cardType is specified in URL, set it as the selected card
    else if (cardTypeParam && ['quick_cash', 'cash_king', 'jackpot_millions', 'mega_millions'].includes(cardTypeParam)) {
      setSelectedCard(cardTypeParam);
      setCreatorMode(false);
    }
  }, [location]);
  
  const handleCardSelect = (cardId) => {
    console.log('Selecting card:', cardId);
    setSelectedCard(cardId);
    
    // Use RESTful URL navigation to the specific card
    // For custom games (starting with 'game_'), use the game ID directly in the URL
    if (cardId && cardId.startsWith('game_')) {
      navigate(`/modular-profile/games/scratch-cards/${cardId}`, { replace: true });
    } else {
      // For predefined games, use the card type in the URL
      navigate(`/modular-profile/games/scratch-cards/${cardId}`, { replace: true });
    }
  };
  
  // Handle entering creator mode
  const enterCreatorMode = (template = null) => {
    // Use the new RESTful URL structure
    if (template) {
      // Format: /modular-profile/games/scratch-cards/templates/template-name
      navigate(`/modular-profile/games/scratch-cards/templates/${template}`, { replace: true });
    } else {
      // Format: /modular-profile/games/scratch-cards/create
      navigate(`/modular-profile/games/scratch-cards/create`, { replace: true });
    }
    
    // Set creator mode
    setCreatorMode(true);
    setTemplateId(template);
  };
  
  // Handle exiting creator mode
  const exitCreatorMode = () => {
    // Use the new RESTful URL structure
    // Return to scratch cards listing
    navigate(`/modular-profile/games/scratch-cards`, { replace: true });
    
    // Reset creator mode
    setCreatorMode(false);
    setTemplateId(null);
  };
  
  const resetSelectedCard = () => {
    console.log('Resetting selected card');
    // First reset the state
    setSelectedCard(null);
    
    // Then navigate directly to the scratch cards listing using RESTful URL structure
    // No need for setTimeout as we're navigating directly
    navigate('/modular-profile/games/scratch-cards', { replace: true });
  };
  
  // Debug function to check localStorage directly
  const debugCheckLocalStorage = () => {
    try {
      const rawData = localStorage.getItem('userCreatedGames');
      console.log('DIRECT DEBUG: Raw localStorage data:', rawData);
      
      if (rawData) {
        const parsedGames = JSON.parse(rawData);
        console.log('DIRECT DEBUG: Parsed games:', parsedGames);
        return parsedGames;
      } else {
        console.warn('DIRECT DEBUG: No data in localStorage');
        return [];
      }
    } catch (error) {
      console.error('DIRECT DEBUG: Error parsing localStorage data:', error);
      return [];
    }
  };
  
  // Fetch user-created games from localStorage
  useEffect(() => {
    try {
      // Force a refresh of localStorage data
      const directCheck = debugCheckLocalStorage();
      
      // Try to parse from localStorage as well
      const savedGames = JSON.parse(localStorage.getItem('userCreatedGames') || '[]');
      console.log('DEBUG: Raw localStorage data:', localStorage.getItem('userCreatedGames'));
      console.log('DEBUG: Parsed user-created games:', savedGames);
      
      if (savedGames && savedGames.length > 0) {
        setUserCreatedGames(savedGames);
        console.log('DEBUG: Set user-created games state with', savedGames.length, 'games');
      } else {
        console.warn('DEBUG: No user-created games found in localStorage');
        
        // Try to create a test game if none exist
        if (directCheck.length === 0) {
          console.log('DEBUG: Creating a test game since none exist');
          const testGame = {
            id: `game_test_${Date.now()}`,
            title: 'Test Game',
            description: 'This is a test game created automatically',
            status: 'PUBLISHED',
            publishedAt: new Date(),
            cardType: 'custom_game'
          };
          
          localStorage.setItem('userCreatedGames', JSON.stringify([testGame]));
          setUserCreatedGames([testGame]);
        }
      }
      
      // Check if the URL contains a game ID and select it if it does
      const pathParts = location.pathname.split('/');
      const lastPart = pathParts[pathParts.length - 1];
      if (lastPart && lastPart.startsWith('game_')) {
        const gameId = lastPart;
        const game = savedGames.find(g => g.id === gameId);
        if (game) {
          setSelectedCard(gameId);
          console.log('Auto-selected game from URL:', game.title);
        }
      }
    } catch (error) {
      console.error('Error loading user-created games:', error);
    }
  }, [location.pathname]);

  // Store the component instance in window for access from tab navigation
  useEffect(() => {
    window.scratchCardComponent = { resetSelectedCard };
    return () => {
      delete window.scratchCardComponent;
    };
  }, []);
  
  // Function to render prize circles for each card type
  const renderPrizeCircles = (cardType) => {
    let prizeValues = [];
    
    if (cardType === 'quickCash') {
      prizeValues = ['5', '50', '500'];
    } else if (cardType === 'cashKing') {
      prizeValues = ['100', '1K', '10K', '100K'];
    } else if (cardType === 'jackpotMillions') {
      prizeValues = ['1K', '5K', '25K', '100K', '250K'];
    } else if (cardType === 'megaMillions') {
      prizeValues = MEGA_MILLIONS_CONFIG.prizes;
    }
    
    return (
      <Flex justify="center" mt={2} mb={4} wrap="wrap">
        {prizeValues.map((value, index) => (
          <Box 
            key={index} 
            bg="rgba(0,0,0,0.5)" 
            borderRadius="full" 
            size="45px" 
            width="45px"
            height="45px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            mx={1}
            border="1px solid rgba(255,255,255,0.3)"
          >
            <Text 
              fontSize="xs" 
              fontWeight="bold" 
              color="white"
            >
              {value}
            </Text>
          </Box>
        ))}
      </Flex>
    );
  };
  
  return (
    <Box height="auto" minHeight="700px" overflow="auto" pb={8}>
      {/* Creator Mode */}
      {/* We don't need this block as we'll handle selected cards in the else branch below */}
      {!creatorMode && !selectedCard ? (
        <Box width="100%">

          <Box 
            width="100%" 
            overflowX="auto" 
            pb={4}
            css={{
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0,0,0,0.1)',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '4px',
              },
              // Add horizontal scrolling indicator
              '&::after': {
                content: '"→"',
                position: 'absolute',
                right: '10px',
                bottom: '0',
                color: 'rgba(255,255,255,0.5)',
                fontSize: '20px',
                animation: 'fadeInOut 1.5s infinite',
              },
            }}
          >
            <Flex alignItems="center" justifyContent="flex-start" gap="15px" overflowX="auto" pb={4}>
              {/* QUICK CASH CARD */}
              <Box 
                width="31%"
                mr={"2%"}
                flexShrink={0}
                onClick={() => handleCardSelect('quick_cash')}
                cursor="pointer"
                transition="transform 0.3s"
                _hover={{ transform: 'scale(1.02)' }}
              >
                <ScratchCardDisplay 
                  cardType="quick_cash"
                  isPreview={true}
                  onPlay={() => handleCardSelect('quick_cash')}
                />
              </Box>
              
              {/* CASH KING CARD */}
              <Box 
                width="31%"
                mr={"2%"}
                flexShrink={0}
                onClick={() => handleCardSelect('cash_king')}
                cursor="pointer"
                transition="transform 0.3s"
                _hover={{ transform: 'scale(1.02)' }}
              >
                <ScratchCardDisplay 
                  cardType="cash_king"
                  isPreview={true}
                  onPlay={() => handleCardSelect('cash_king')}
                />
              </Box>
              
              {/* JACKPOT MILLIONS CARD */}
              <Box 
                width="31%"
                mr={"2%"}
                flexShrink={0}
                onClick={() => handleCardSelect('jackpot_millions')}
                cursor="pointer"
                transition="transform 0.3s"
                _hover={{ transform: 'scale(1.02)' }}
              >
                <ScratchCardDisplay 
                  cardType="jackpot_millions"
                  isPreview={true}
                  onPlay={() => handleCardSelect('jackpot_millions')}
                />
              </Box>
              
              {/* MEGA MILLIONS CARD */}
              <Box 
                width="31%"
                mr={"2%"}
                flexShrink={0}
                onClick={() => handleCardSelect('mega_millions')}
                cursor="pointer"
                transition="transform 0.3s"
                _hover={{ transform: 'scale(1.02)' }}
              >
                <ScratchCardDisplay 
                  cardType="mega_millions"
                  isPreview={true}
                  onPlay={() => handleCardSelect('mega_millions')}
                />
              </Box>
              
              {/* USER CREATED GAMES - Displayed alongside predefined games */}
              {userCreatedGames && userCreatedGames.map((game) => (
                <Box 
                  key={game.id}
                  width="31%"
                  mr={"2%"}
                  flexShrink={0}
                  onClick={() => handleCardSelect(game.id)}
                  cursor="pointer"
                  transition="transform 0.3s"
                  _hover={{ transform: 'scale(1.02)' }}
                  mb={2}
                >
                  <ScratchCardDisplay 
                    cardType={game.id}
                    isPreview={true}
                    onPlay={() => handleCardSelect(game.id)}
                  />
                </Box>
              ))}
              
              {/* DEBUG CONTROLS */}
              <Box width="100%" mb={4} p={3} bg="red.700" color="white" borderRadius="md">
                <Heading size="sm" mb={2}>Debug Controls</Heading>
                <HStack spacing={4}>
                  <Button 
                    size="sm" 
                    colorScheme="blue"
                    onClick={() => {
                      // Create a test game and save it directly to localStorage
                      const testGame = {
                        id: `game_${Date.now()}`,
                        title: `Test Game ${new Date().toLocaleTimeString()}`,
                        description: 'This is a test game created directly from the debug controls',
                        status: 'PUBLISHED',
                        publishedAt: new Date(),
                        cardType: 'custom_game'
                      };
                      
                      // Get existing games
                      const savedGames = JSON.parse(localStorage.getItem('userCreatedGames') || '[]');
                      savedGames.push(testGame);
                      
                      // Save to localStorage
                      localStorage.setItem('userCreatedGames', JSON.stringify(savedGames));
                      
                      // Update state
                      setUserCreatedGames([...savedGames]);
                      
                      alert(`Test game created and saved to localStorage. Total games: ${savedGames.length}`);
                    }}
                  >
                    Create Test Game
                  </Button>
                  
                  <Button 
                    size="sm" 
                    colorScheme="green"
                    onClick={() => {
                      // Force reload games from localStorage
                      const savedGames = JSON.parse(localStorage.getItem('userCreatedGames') || '[]');
                      setUserCreatedGames([...savedGames]);
                      alert(`Reloaded ${savedGames.length} games from localStorage`);
                    }}
                  >
                    Reload Games
                  </Button>
                  
                  <Button 
                    size="sm" 
                    colorScheme="red"
                    onClick={() => {
                      // Clear all games
                      localStorage.removeItem('userCreatedGames');
                      setUserCreatedGames([]);
                      alert('All games cleared from localStorage');
                    }}
                  >
                    Clear All Games
                  </Button>
                </HStack>
              </Box>
              
              {/* USER CREATED GAMES - Empty placeholder for when no games exist */}
              {userCreatedGames && userCreatedGames.length === 0 && (
                <Box width="100%" p={4} bg="gray.700" color="white" borderRadius="md">
                  <Text>No custom games found. Create one using the wizard!</Text>
                </Box>
              )}
            </Flex>
          </Box>
        </Box>
      ) : (
        <Box p={4}>
          {/* Scratch Card Game */}
          <Box p={4} bg="rgba(0,0,0,0.3)" borderRadius="lg" width="100%">
            {/* When a card is selected, show the full game with controls */}
            {selectedCard ? (
              selectedCard === 'quick_cash' ? (
                <QuickCashGame 
                  inlineMode={true}
                  onBack={resetSelectedCard}
                />
              ) : selectedCard === 'cash_king' ? (
                <ScratchCardDisplay 
                  cardType="cash_king"
                  isPreview={false}
                  onClose={resetSelectedCard}
                />
              ) : selectedCard === 'jackpot_millions' ? (
                <ScratchCardDisplay 
                  cardType="jackpot_millions"
                  isPreview={false}
                  onClose={resetSelectedCard}
                />
              ) : selectedCard === 'mega_millions' ? (
                <ScratchCardDisplay 
                  cardType="mega_millions"
                  isPreview={false}
                  onClose={resetSelectedCard}
                />
              ) : (
                // Handle user-created games
                (() => {
                  // Find the selected user-created game
                  const selectedGame = userCreatedGames.find(game => game.id === selectedCard);
                  
                  if (selectedGame) {
                    return (
                      <Box>
                        <Button 
                          leftIcon={<FaArrowLeft />} 
                          onClick={resetSelectedCard} 
                          mb={4}
                          variant="outline"
                          colorScheme="blue"
                        >
                          Back to Games
                        </Button>
                        
                        <Box p={4} borderWidth="1px" borderRadius="lg" bg="blue.900" color="white">
                          <Heading size="lg" mb={2}>{selectedGame.title || 'Custom Game'}</Heading>
                          <Text mb={4}>{selectedGame.description || 'A custom scratch card game'}</Text>
                          
                          <Flex wrap="wrap" gap={4} mb={4}>
                            <Badge colorScheme="green" p={2} fontSize="md">Custom Game</Badge>
                            <Badge colorScheme="purple" p={2} fontSize="md">Created: {new Date(selectedGame.publishedAt).toLocaleDateString()}</Badge>
                          </Flex>
                          
                          <Box mt={6} p={4} bg="rgba(0,0,0,0.2)" borderRadius="md">
                            <Heading size="md" mb={4}>Game Details</Heading>
                            
                            {selectedGame.prizeTiers && selectedGame.prizeTiers.length > 0 && (
                              <Box mb={4}>
                                <Heading size="sm" mb={2}>Prize Tiers</Heading>
                                {selectedGame.prizeTiers.map((tier, index) => (
                                  <Text key={index}>
                                    {tier.name || `Tier ${index + 1}`}: {tier.amount} {tier.tokenType}
                                  </Text>
                                ))}
                              </Box>
                            )}
                            
                            <Button 
                              mt={4} 
                              colorScheme="green" 
                              leftIcon={<FaPlay />}
                              onClick={() => alert('Game play feature coming soon!')}
                            >
                              Play Game
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    );
                  }
                  
                  return (
                    <Center p={8}>
                      <Text>Game not found</Text>
                    </Center>
                  );
                })()
              )
            ) : (
              <Center p={8}>
                <Text>Select a scratch card to play</Text>
              </Center>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const CardGame = () => {
  return (
    <Box p={3}>
      <MainCardGame />
    </Box>
  );
};

// This component is no longer needed as we're using the imported SlotMachine component

const GamesContent = ({ activeGame }) => {
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // State for active tab index - initialize to 0 (Island Map)
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  
  // Set initial tab index based on URL path or activeGame prop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // This effect only runs once on component mount to set the initial tab
    // Map URL paths to tab indexes
    const pathToTabIndex = {
      '/modular-profile/games': 0,                // Island Map
      '/modular-profile/games/island-map': 0,     // Island Map (explicit path)
      '/modular-profile/games/scratch-cards': 1,  // Scratch Cards
      '/modular-profile/games/card-game': 2,      // Card Game
      '/modular-profile/games/gift-box': 3,       // Gift Box
      '/modular-profile/games/fortune-wheel': 4,  // Fortune Wheel
      '/modular-profile/games/slots': 5,          // Slots
      '/modular-profile/games/daily-bonus': 6     // Daily Bonus
    };
    
    // Check if current path matches any of our defined paths
    const currentPath = location.pathname;
    console.log('Initial path:', currentPath);
    
    let initialTabIndex = 0; // Default to Island Map
    
    // First check for exact path matches
    if (pathToTabIndex[currentPath] !== undefined) {
      initialTabIndex = pathToTabIndex[currentPath];
    }
    // Check for scratch card specific paths (with card type)
    else if (currentPath.startsWith('/modular-profile/games/scratch-cards/')) {
      initialTabIndex = 1; // Scratch Card tab
    }
    // Fallback to activeGame prop for backward compatibility
    else if (activeGame === 'scratchcard') {
      initialTabIndex = 1; // Scratch Card tab
    }
    
    console.log('Setting initial tab index to:', initialTabIndex);
    setActiveTabIndex(initialTabIndex);
  }, []); // Empty dependency array means this only runs once on mount
  
  // Handle tab change with RESTful URL navigation
  const handleTabChange = (index) => {
    console.log('Tab change requested to index:', index);
    // Map tab index to URL path
    const tabPaths = [
      '/modular-profile/games/island-map',    // Island Map (index 0) - explicit path to avoid confusion
      '/modular-profile/games/scratch-cards', // Scratch Cards (index 1)
      '/modular-profile/games/card-game',     // Card Game (index 2)
      '/modular-profile/games/gift-box',      // Gift Box (index 3)
      '/modular-profile/games/fortune-wheel', // Fortune Wheel (index 4)
      '/modular-profile/games/slots',         // Slots (index 5)
      '/modular-profile/games/daily-bonus'    // Daily Bonus (index 6)
    ];
    
    // Update the active tab index immediately to prevent UI lag
    setActiveTabIndex(index);
    
    // Use direct DOM manipulation to force the tab to be shown immediately
    // This prevents the Island Map tab from showing first
    document.querySelectorAll('.chakra-tabs__tab-panel').forEach((panel, i) => {
      if (i === index) {
        panel.style.display = 'block';
      } else {
        panel.style.display = 'none';
      }
    });
    
    // Special handling for Scratch Card tab (index 1)
    if (index === 1) {
      console.log('Scratch Card tab clicked');
      
      // Check if we need to reset the card selection
      if (window.scratchCardComponent && window.scratchCardComponent.resetSelectedCard) {
        console.log('Resetting selected card');
        window.scratchCardComponent.resetSelectedCard();
      } else {
        // If no card is selected, just navigate directly
        navigate(tabPaths[1], { replace: true });
      }
    } else {
      // For other tabs, navigate directly to their path
      console.log('Other tab clicked:', index);
      navigate(tabPaths[index], { replace: true });
    }
  };
  
  return (
    <VStack spacing={4} align="stretch">
      <Heading color="white" size="md" display="flex" alignItems="center">
        <Icon as={FaGamepad} mr={2} color="green.400" />
        Games
      </Heading>
      
      <Box 
        p={5} 
        bg="linear-gradient(135deg, rgba(20, 30, 40, 0.85) 0%, rgba(10, 20, 30, 0.95) 100%)"
        borderRadius="xl"
        boxShadow="0 10px 30px -10px rgba(0, 230, 118, 0.3)"
        backdropFilter="blur(10px)"
        position="relative"
        overflow="hidden"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '4px',
          bg: 'linear-gradient(90deg, rgba(0, 230, 118, 0.8), rgba(0, 230, 118, 0.2))',
          borderTopRadius: 'xl'
        }}
      >
        <Tabs 
          variant="soft-rounded" 
          colorScheme="green" 
          size="md" 
          isFitted 
          index={activeTabIndex} 
          onChange={handleTabChange}
          isLazy={false}
          defaultIndex={activeTabIndex}
        >
          <TabList mb={4} overflowX="auto" css={{
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(0, 0, 0, 0.1)',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0, 230, 118, 0.3)',
              borderRadius: '10px',
            },
          }}>
            <Tab 
              _selected={{ 
                bg: "rgba(0, 230, 118, 0.2)",
                color: "#00e676",
                boxShadow: "0 4px 12px rgba(0, 230, 118, 0.2)"
              }}
            >
              <Icon as={GiTreasureMap} mr={2} />
              Island Map
            </Tab>
            <Tab 
              _selected={{ 
                bg: "rgba(0, 230, 118, 0.2)",
                color: "#00e676",
                boxShadow: "0 4px 12px rgba(0, 230, 118, 0.2)"
              }}
              onClick={() => {
                // If a card is selected, reset to show all cards
                if (window.scratchCardComponent && window.scratchCardComponent.resetSelectedCard) {
                  window.scratchCardComponent.resetSelectedCard();
                }
              }}
            >
              <Icon as={GiCardRandom} mr={2} />
              Scratch Card
            </Tab>
            <Tab 
              _selected={{ 
                bg: "rgba(0, 230, 118, 0.2)",
                color: "#00e676",
                boxShadow: "0 4px 12px rgba(0, 230, 118, 0.2)"
              }}
            >
              <Icon as={GiCardPlay} mr={2} />
              Card Game
            </Tab>
            <Tab 
              _selected={{ 
                bg: "rgba(0, 230, 118, 0.2)",
                color: "#00e676",
                boxShadow: "0 4px 12px rgba(0, 230, 118, 0.2)"
              }}
            >
              <Icon as={FaGift} mr={2} />
              Gift Box
            </Tab>
            <Tab 
              _selected={{ 
                bg: "rgba(0, 230, 118, 0.2)",
                color: "#00e676",
                boxShadow: "0 4px 12px rgba(0, 230, 118, 0.2)"
              }}
            >
              <Icon as={FaTrophy} mr={2} />
              Fortune Wheel
            </Tab>
            <Tab 
              _selected={{ 
                bg: "rgba(0, 230, 118, 0.2)",
                color: "#00e676",
                boxShadow: "0 4px 12px rgba(0, 230, 118, 0.2)"
              }}
            >
              <Icon as={GiPokerHand} mr={2} />
              Slots
            </Tab>
            <Tab 
              _selected={{ 
                bg: "rgba(0, 230, 118, 0.2)",
                color: "#00e676",
                boxShadow: "0 4px 12px rgba(0, 230, 118, 0.2)"
              }}
            >
              <Icon as={FaCalendarAlt} mr={2} />
              Daily Bonus
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel p={0}>
              <IslandMapGame />
            </TabPanel>
            <TabPanel p={0}>
              <ScratchCardGame />
            </TabPanel>
            <TabPanel p={0}>
              <Box maxH="700px" overflow="auto">
                <CardGame />
              </Box>
            </TabPanel>
            <TabPanel p={0}>
              <Box maxH="700px" overflow="auto">
                <GiftBoxGame />
              </Box>
            </TabPanel>
            <TabPanel p={0}>
              <Box maxH="700px" overflow="auto">
                <FortuneWheel />
              </Box>
            </TabPanel>
            <TabPanel p={0}>
              <Box maxH="700px" overflow="auto">
                <SlotMachine />
              </Box>
            </TabPanel>
            <TabPanel p={0}>
              <Box maxH="700px" overflow="auto">
                <DailyPrizeCard />
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </VStack>
  );
};

export default GamesContent;
